import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getDiploma, finalizeProject, type DiplomaData } from '../api/projects';
import { AppLayout } from '../components/AppLayout';
import { BigButton, LoadingDots } from '../components/ChildFriendly';
import { Confetti } from '../chat/Confetti';

const MAX_ROLE_SCORE = 105; // score + bonus teorik üst sınır

export default function DiplomaPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<DiplomaData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [finalizing, setFinalizing] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    setLoading(true);
    getDiploma(Number(projectId))
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(
            err?.response?.data?.detail || 'Diploma bilgisi yüklenemedi.',
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [projectId]);

  // Başarılı görüntülemede kısa konfeti
  useEffect(() => {
    if (data && data.report.overall_status === 'passed' && data.diploma.pdf_url) {
      setShowConfetti(true);
      const t = setTimeout(() => setShowConfetti(false), 1600);
      return () => clearTimeout(t);
    }
    return;
  }, [data]);

  async function handleFinalize() {
    if (!projectId) return;
    setFinalizing(true);
    setError('');
    try {
      await finalizeProject(Number(projectId));
      // Verileri tazele
      const fresh = await getDiploma(Number(projectId));
      setData(fresh);
    } catch (err) {
      const detail = (
        err as { response?: { data?: { detail?: string } } }
      )?.response?.data?.detail;
      setError(detail || 'Diploma oluşturulamadı.');
    } finally {
      setFinalizing(false);
    }
  }

  if (loading) {
    return (
      <AppLayout title="Diploma" width="narrow">
        <LoadingDots message="Diploma hazırlanıyor" />
      </AppLayout>
    );
  }

  if (error || !data) {
    return (
      <AppLayout title="Diploma">
        <div role="alert" className="error">
          {error || 'Diploma bulunamadı.'}
        </div>
        <BigButton variant="ghost" onClick={() => navigate(-1)}>
          ← Geri dön
        </BigButton>
      </AppLayout>
    );
  }

  const isPassed = data.report.overall_status === 'passed';

  return (
    <>
      <AppLayout
        title="Mezuniyet Projesi Diploması"
        width="narrow"
        actions={
          <BigButton
            variant="ghost"
            onClick={() => navigate(`/projects/${data.project.id}`)}
          >
            ← Projeye dön
          </BigButton>
        }
      >
        <div className="stack">
          <div className="diploma-hero" data-overall={data.report.overall_status}>
            <span className="diploma-hero__emoji" aria-hidden="true">
              {isPassed ? '🎓' : '💪'}
            </span>
            <h1 className="diploma-hero__title">
              {isPassed ? 'Tebrikler, geçtin!' : 'Bu projede öğrendiklerin değerli'}
            </h1>
            <p className="diploma-hero__subtitle">
              {isPassed
                ? `${data.student.display_name}, diplomana hak kazandın.`
                : 'Bir sonraki projede daha iyi olacak.'}
            </p>

            <div className="average-badge">
              <span className="average-badge__label">Ortalama Puan</span>
              <span className="average-badge__value">
                {data.report.average_score}
              </span>
              <span className="average-badge__max">/ 100 üzerinden</span>
            </div>
          </div>

          <section className="card stack" aria-labelledby="problem-recap-heading">
            <h2 id="problem-recap-heading" className="section-title">
              📝 Projen
            </h2>
            <p style={{ fontSize: 'var(--font-size-md)', lineHeight: 'var(--line-base)' }}>
              {data.project.problem_text}
            </p>
          </section>

          <section className="card stack" aria-labelledby="score-dist-heading">
            <h2 id="score-dist-heading" className="section-title">
              🎯 Rol Bazlı Puanlar
            </h2>
            <div className="score-bar-list">
              {data.role_sessions.map((r) => {
                const score = r.status === 'passed'
                  ? (r.score ?? 0) + r.bonus
                  : 0;
                const widthPct = Math.min(
                  100,
                  (score / MAX_ROLE_SCORE) * 100,
                );
                const isRolePassed = r.status === 'passed';
                return (
                  <div key={r.id} className="score-bar-row">
                    <span className="score-bar-row__name">
                      {r.role_name}
                      {r.bonus > 0 && (
                        <small
                          style={{
                            marginLeft: 8,
                            color: 'var(--accent)',
                            fontWeight: 700,
                          }}
                        >
                          (+{r.bonus} bonus 🏆)
                        </small>
                      )}
                    </span>
                    <span className="score-bar-row__score">
                      {r.status === 'passed' ? (
                        <>
                          {score}
                          <small> / {MAX_ROLE_SCORE}</small>
                        </>
                      ) : (
                        <span className="status-badge status-failed">
                          Başarısız
                        </span>
                      )}
                    </span>
                    <div className="score-bar-track">
                      <div
                        className={`score-bar-fill ${isRolePassed ? 'is-passed' : 'is-failed'}`}
                        style={{ width: `${widthPct}%` }}
                        role="progressbar"
                        aria-valuenow={score}
                        aria-valuemin={0}
                        aria-valuemax={MAX_ROLE_SCORE}
                        aria-label={`${r.role_name} puanı`}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          <section className="card stack" aria-labelledby="meta-heading">
            <h2 id="meta-heading" className="section-title">
              📋 Bilgiler
            </h2>
            <div className="diploma-meta">
              <div className="diploma-meta__item">
                <span className="diploma-meta__label">Öğrenci</span>
                <span className="diploma-meta__value">
                  {data.student.display_name}
                </span>
              </div>
              <div className="diploma-meta__item">
                <span className="diploma-meta__label">Sınıf</span>
                <span className="diploma-meta__value">
                  {data.student.classroom_name}
                </span>
              </div>
              <div className="diploma-meta__item">
                <span className="diploma-meta__label">Rol Sayısı</span>
                <span className="diploma-meta__value">
                  {data.role_sessions.length}
                </span>
              </div>
              {data.project.finished_at && (
                <div className="diploma-meta__item">
                  <span className="diploma-meta__label">Tamamlanma</span>
                  <span className="diploma-meta__value">
                    {new Date(data.project.finished_at).toLocaleDateString('tr-TR')}
                  </span>
                </div>
              )}
            </div>
          </section>

          <div className="diploma-actions">
            {data.diploma.pdf_url ? (
              <a
                href={data.diploma.pdf_url}
                className="btn btn--primary btn--lg"
                target="_blank"
                rel="noreferrer"
                download
              >
                <span aria-hidden="true">📥</span> Diploma PDF'ini İndir
              </a>
            ) : (
              <BigButton
                variant="primary"
                icon="🎓"
                onClick={handleFinalize}
                loading={finalizing}
              >
                Diploma PDF'ini Oluştur
              </BigButton>
            )}
            <BigButton
              variant="ghost"
              onClick={() => navigate('/')}
            >
              Panele dön
            </BigButton>
          </div>

          {!isPassed && (
            <div role="status" className="success-banner-inline">
              <span aria-hidden="true">💡</span>{' '}
              Her başarısız deneme değerli bir öğrenme. Bir dahaki sefere
              farklı kanıtlarla dene.
            </div>
          )}

          {!data.diploma.pdf_url && !isPassed && (
            <p className="text-xs text-muted text-center">
              PDF diploma sadece geçen projeler için oluşturulur.
            </p>
          )}
        </div>
      </AppLayout>
      <Confetti active={showConfetti} onDone={() => setShowConfetti(false)} />
    </>
  );
}
